// THE TWO SPOKEN MESSAGES of inbound call forwarding, editable per club by Kyck and by the club:
//   - the WHISPER (heard only by whoever answers the forwarded call), and
//   - the MISSED-CALL message (heard by the caller when nobody takes the call).
// PURE: the template validator, the renderer and the previews. Stage 3's outbound staff whisper reuses this.
//
// ⚠️⚠️ THE KEY PROMPT IS NOT PART OF THE TEMPLATE. The whisper is rendered here, and voiceNcco's
// `spokenWhisper` appends "Press any key to take the call." AFTER it, always. A template cannot contain it,
// remove it or reorder it: it is what stops a voicemail box taking the call. (Stage 3's recording notice is
// a separate, NON-editable message: its wording is a legal matter.)
// ⚠️ These messages go to a TEXT-TO-SPEECH engine that reads `<speak>` as SSML, so markup is refused in a
// template and stripped from every substituted value (a lead's name is untrusted input).

export const MESSAGE_MAX_CHARS = 200;

/**
 * The DEFAULT whisper (shown as the editor's hint). The real default is `defaultWhisper`, which also handles an
 * unknown source ("a KyckConnect lead") and an unknown caller ("an unknown caller on the {pool} line").
 */
export const DEFAULT_WHISPER_TEMPLATE = 'Call from {lead_name}, a {source} lead for {club}.';
export const DEFAULT_MISSED_TEMPLATE = 'Thanks for calling {club}. Sorry we missed you. Someone from the team will get back to you shortly.';

export const WHISPER_PLACEHOLDERS = ['lead_name', 'club', 'source', 'pool'] as const;
export const MISSED_PLACEHOLDERS = ['club'] as const;
/** Stage 3: the whisper staff hear on an OUTBOUND Speed to Lead call, before the key prompt. */
export const DEFAULT_OUTBOUND_TEMPLATE = 'New {source} lead for {club}: {lead_name}.';
export const OUTBOUND_PLACEHOLDERS = ['lead_name', 'club', 'source'] as const;

export type MessageKind = 'whisper' | 'missed' | 'outbound';
const ALLOWED: Record<MessageKind, readonly string[]> = { whisper: WHISPER_PLACEHOLDERS, missed: MISSED_PLACEHOLDERS, outbound: OUTBOUND_PLACEHOLDERS };
const DEFAULTS: Record<MessageKind, string> = { whisper: DEFAULT_WHISPER_TEMPLATE, missed: DEFAULT_MISSED_TEMPLATE, outbound: DEFAULT_OUTBOUND_TEMPLATE };
const LABELS: Record<MessageKind, string> = { whisper: 'The whisper', missed: 'The missed-call message', outbound: 'The outbound whisper' };

export const messageDefault = (kind: MessageKind): string => DEFAULTS[kind];
export const messagePlaceholders = (kind: MessageKind): readonly string[] => ALLOWED[kind];

export type MessageCheck = { ok: true; value: string | null } | { ok: false; error: string };

// A scheme, "www.", or a bare domain with a common TLD: a link read aloud is useless and a lead-in to abuse.
const URL_LIKE = /(https?:|ftp:|www\.|\b[a-z0-9-]+\.(com|net|org|edu|gov|io|co|me|ly|app|dev|au|nz|uk|us)\b)/i;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/**
 * Validate one template. `null`, empty or whitespace-only means "use the default" (value: null).
 * Normalises whitespace. Rejects: markup, URLs, control characters, unknown or malformed placeholders and
 * anything over {@link MESSAGE_MAX_CHARS} after normalising.
 */
export function validateMessageTemplate(kind: MessageKind, raw: unknown): MessageCheck {
  if (raw === null || raw === undefined) return { ok: true, value: null };
  if (typeof raw !== 'string') return { ok: false, error: `${LABELS[kind]} must be text.` };
  if (CONTROL_CHARS.test(raw.replace(/[\t\r\n]/g, ' '))) return { ok: false, error: `${LABELS[kind]} contains characters that cannot be spoken.` };
  const text = raw.replace(/\s+/g, ' ').trim();
  if (text === '') return { ok: true, value: null };
  if (text.length > MESSAGE_MAX_CHARS) return { ok: false, error: `${LABELS[kind]} can be at most ${MESSAGE_MAX_CHARS} characters (it is ${text.length}).` };
  if (/[<>]/.test(text) || /&[#a-z0-9]+;/i.test(text)) return { ok: false, error: `${LABELS[kind]} must be plain text: no markup.` };
  if (URL_LIKE.test(text)) return { ok: false, error: `${LABELS[kind]} cannot contain a web address.` };
  const allowed = ALLOWED[kind];
  const stripped = text.replace(/\{([^{}]*)\}/g, (_m, name: string) => {
    return allowed.includes(name) ? '' : '\u0000';
  });
  if (stripped.includes('\u0000')) {
    const bad = [...text.matchAll(/\{([^{}]*)\}/g)].map((m) => m[1]!).filter((n) => !allowed.includes(n));
    return { ok: false, error: `${LABELS[kind]} has an unknown placeholder: {${bad[0] ?? ''}}. Use only ${allowed.map((a) => `{${a}}`).join(', ')}.` };
  }
  if (/[{}]/.test(stripped)) return { ok: false, error: `${LABELS[kind]} has a stray brace. Write placeholders like {club}.` };
  return { ok: true, value: text };
}

/** A substituted value is untrusted: strip markup and braces, say "and" for "&", squash whitespace. */
export function sanitizeSpokenValue(v: string): string {
  return v.replace(/&/g, ' and ').replace(/[<>{}]/g, '').replace(/\s+/g, ' ').trim();
}

export interface WhisperValues {
  /** The lead's first name, when known. */
  leadName: string | null;
  club: string | null;
  /** The lead's source in plain words ("Facebook", "website"), or null when unknown. */
  source: string | null;
  /** The number pool's name, for an unknown caller. */
  pool?: string | null;
  /** False = a stranger (no conversation or intake lead). Defaults to true when a name is known. */
  callerKnown?: boolean;
}

const article = (word: string) => (/^[aeiou]/i.test(word) ? 'an' : 'a');

/**
 * The default whisper. Known caller: "Call from {lead_name}, a {source} lead for {club}.", or "a KyckConnect lead"
 * when the source is unknown. Unknown caller: "Call from an unknown caller on the {pool} line.".
 */
export function defaultWhisper(v: WhisperValues): string {
  const known = v.callerKnown ?? (v.leadName ?? '').trim() !== '';
  if (!known) return `Call from an unknown caller on the ${sanitizeSpokenValue(v.pool ?? '') || 'KyckConnect'} line.`;
  const name = sanitizeSpokenValue(v.leadName ?? '') || 'an unknown caller';
  const club = sanitizeSpokenValue(v.club ?? '') || 'the club';
  const source = sanitizeSpokenValue(v.source ?? '') || 'KyckConnect';
  return `Call from ${name}, ${article(source)} ${source} lead for ${club}.`;
}

/** Render a template (null = the default) with fallbacks: an unknown caller, "us" for a missing club. */
export function renderWhisper(template: string | null, v: WhisperValues): string {
  if (template === null) return defaultWhisper(v);
  const values: Record<string, string> = {
    lead_name: sanitizeSpokenValue(v.leadName ?? '') || 'an unknown caller',
    club: sanitizeSpokenValue(v.club ?? '') || 'the club',
    source: sanitizeSpokenValue(v.source ?? '') || 'an unknown source',
    pool: sanitizeSpokenValue(v.pool ?? '') || 'KyckConnect',
  };
  return fill(template, values);
}

export function renderMissed(template: string | null, club: string | null): string {
  return fill(template ?? DEFAULT_MISSED_TEMPLATE, { club: sanitizeSpokenValue(club ?? '') || 'us' });
}

/** The outbound staff whisper: the club's template (null = the default) with safe fallbacks. The key prompt is added later. */
export function renderOutboundWhisper(template: string | null, v: { leadName: string | null; club: string | null; source: string | null }): string {
  return fill(template ?? DEFAULT_OUTBOUND_TEMPLATE, {
    lead_name: sanitizeSpokenValue(v.leadName ?? '') || 'an unknown lead',
    club: sanitizeSpokenValue(v.club ?? '') || 'the club',
    source: sanitizeSpokenValue(v.source ?? '') || 'KyckConnect',
  });
}

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{([^{}]*)\}/g, (_m, name: string) => values[name] ?? '').replace(/\s+/g, ' ').trim();
}

/** The lead's source in plain words for the whisper ("Facebook", "website"). Null when unknown. */
export function leadSourceLabel(source: string | null | undefined): string | null {
  switch ((source ?? '').trim().toLowerCase()) {
    case 'meta_lead_form':
    case 'facebook':
    case 'meta':
      return 'Facebook';
    case 'instagram':
      return 'Instagram';
    case 'website':
      return 'website';
    case 'phone':
      return 'phone';
    case 'walk_in':
      return 'walk-in';
    default:
      return null; // 'other', empty, or anything we cannot say in plain words
  }
}

/** The sample values the editors preview with. */
export const PREVIEW_VALUES: WhisperValues = { leadName: 'Sarah', club: 'Holland Park', source: 'Facebook', pool: 'AF East', callerKnown: true };

export interface MessageSettings {
  /** null = the default. */
  whisperMessage: string | null;
  missedCallMessage: string | null;
  /** Stage 3 (outbound staff whisper). undefined in an input = keep the stored one. */
  outboundWhisperMessage?: string | null;
}

/** The messages in one pass, for the settings write. */
export function validateMessageSettings(input: { whisperMessage?: unknown; missedCallMessage?: unknown; outboundWhisperMessage?: unknown }):
  | { ok: true; value: MessageSettings }
  | { ok: false; error: string } {
  const w = validateMessageTemplate('whisper', input.whisperMessage);
  if (!w.ok) return w;
  const m = validateMessageTemplate('missed', input.missedCallMessage);
  if (!m.ok) return m;
  if (input.outboundWhisperMessage === undefined) return { ok: true, value: { whisperMessage: w.value, missedCallMessage: m.value } };
  const o = validateMessageTemplate('outbound', input.outboundWhisperMessage);
  if (!o.ok) return o;
  return { ok: true, value: { whisperMessage: w.value, missedCallMessage: m.value, outboundWhisperMessage: o.value } };
}
